// The contract both Linux engines (v86 and CheerpX) implement, plus small shared helpers.
// Ported from EndisOS src/linux/engine.ts. An engine exposes:
//   capabilities { id, name, networking: "wisp" | "tailscale" | "none", graphics, resizableConsole }
//   state / error, start(machine, console, signal), sendInput(bytes), stop(), importFile(name, blob),
//   exportFile(name), listExports(), onState(fn), onActivity(fn) (-> unsubscribe functions).

export const STATES = ["idle", "checking", "loading-runtime", "opening-disk", "booting", "running", "stopping", "stopped", "error"];
export const STATE_LABEL = {
	idle: "Ready", checking: "Checking", "loading-runtime": "Loading runtime", "opening-disk": "Opening disk",
	booting: "Booting", running: "Running", stopping: "Saving", stopped: "Stopped", error: "Error",
};
export const ERROR_CODES = [
	"NOT_ISOLATED", "NO_SHARED_MEMORY", "NOT_ACCEPTED", "OFFLINE", "RUNTIME_LOAD_FAILED", "RUNTIME_INIT_FAILED",
	"IMAGE_UNREACHABLE", "IMAGE_INVALID", "STORAGE_FAILED", "PROCESS_FAILED", "NETWORK_LOGIN_FAILED", "ABORTED", "LOCKED",
];

export class LinuxError extends Error {
	constructor(code, message, cause) {
		super(message);
		this.name = "LinuxError";
		this.code = ERROR_CODES.includes(code) ? code : "PROCESS_FAILED";
		if (cause !== undefined) this.cause = cause;
	}
}
export function toLinuxError(code, err, prefix) {
	if (err instanceof LinuxError) return err;
	const msg = err && err.message ? err.message : String(err);
	return new LinuxError(code, prefix ? prefix + ": " + msg : msg, err);
}

// Tiny event set: add() returns an unsubscribe function; a throwing listener can't break the others.
export function listeners() {
	const set = new Set();
	return {
		add(fn) { set.add(fn); return () => set.delete(fn); },
		emit(v) { for (const fn of [...set]) { try { fn(v); } catch (e) {} } },
		clear() { set.clear(); },
	};
}

// Rejects with ABORTED as soon as `signal` fires, even if `promise` never settles (a hung import,
// fetch or vendor call). Does not cancel the underlying work.
export function withAbort(promise, signal, message = "Start cancelled.") {
	if (signal.aborted) return Promise.reject(signal.reason instanceof LinuxError ? signal.reason : new LinuxError("ABORTED", message));
	return new Promise((resolve, reject) => {
		const onAbort = () => reject(signal.reason instanceof LinuxError ? signal.reason : new LinuxError("ABORTED", message));
		signal.addEventListener("abort", onAbort, { once: true });
		promise.then(
			(v) => { signal.removeEventListener("abort", onAbort); resolve(v); },
			(e) => { signal.removeEventListener("abort", onAbort); reject(e); }
		);
	});
}

export const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
export function fmtBytes(n) {
	if (!n) return "0 MB";
	const mb = n / 1048576;
	return mb >= 1024 ? (mb / 1024).toFixed(2) + " GB" : mb >= 10 ? Math.round(mb) + " MB" : mb.toFixed(1) + " MB";
}
